import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send } from "lucide-react";

export default function TaskComments({ task, members, refreshActivity }) {
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [mentioned, setMentioned] = useState([]);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!task) return;
    setCommentText("");
    setMentioned([]);
    api.get(`/tasks/${task.task_id}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [task]);

  const postComment = async () => {
    if (!commentText.trim()) return;
    setPosting(true);
    try {
      const res = await api.post(`/tasks/${task.task_id}/comments`, { text: commentText, mentions: mentioned });
      setComments((c) => [...c, res.data]);
      setCommentText("");
      setMentioned([]);
      refreshActivity();
    } catch (e) { toast.error("Failed to post comment"); }
    finally { setPosting(false); }
  };

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5 mb-2">
        <MessageSquare className="h-3.5 w-3.5" /> Comments
      </label>
      <div className="space-y-3 mb-3 max-h-52 overflow-y-auto">
        {comments.length === 0 && <p className="text-xs text-zinc-400">No comments yet. Start the discussion below.</p>}
        {comments.map((c) => (
          <div key={c.comment_id} className="flex gap-2.5" data-testid={`comment-${c.comment_id}`}>
            <Avatar id={c.user_id} name={c.user_name} size={26} />
            <div className="flex-1 min-w-0">
              <p className="text-xs">
                <span className="font-semibold text-zinc-900">{c.user_name}</span>
                <span className="text-zinc-400 ml-2">{new Date(c.created_at).toLocaleString()}</span>
              </p>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap break-words">{c.text}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {members.map((m) => (
          <button key={m.user_id} type="button"
            onClick={() => {
              setCommentText((t) => `${t}${t && !t.endsWith(" ") ? " " : ""}@${m.name.split(" ")[0]} `);
              setMentioned((ids) => (ids.includes(m.user_id) ? ids : [...ids, m.user_id]));
            }}
            className={`text-[11px] px-2 py-0.5 rounded-full border ${mentioned.includes(m.user_id) ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-500 hover:bg-zinc-50"}`}
            data-testid={`mention-${m.user_id}`}>
            @{m.name.split(" ")[0]}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid="comment-input" value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postComment(); } }}
          placeholder="Write a comment…" className="rounded-sm" />
        <Button data-testid="post-comment-button" onClick={postComment} disabled={posting || !commentText.trim()}
          className="bg-zinc-900 hover:bg-zinc-800 rounded-sm px-3">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
